import { regex } from "./constants";
import { isRegexMatch } from "./validators/commonValidators";
import { i18nTranslate } from "@/plugins/i18n";
import { makeToast } from "@/shared/utils/toast";
import useLogging from "@/shared/composables/useLogging";
import includes from "lodash/includes";

const { submitErrorToApi } = useLogging();

/**
 * @example
 * regexLatinSymbolsNumbers.test("a")
 * // => true
 *
 * regexLatinSymbolsNumbers.test("Æ")
 * // => false
 */
export const regexLatinSymbolsNumbers =
  /^[a-zA-Z0-9@~`!@#$%^&*()_ =+\\\\';:"\\/?>.<,-]*$/i;

/**
 * Regex to only allow digits
 *
 * The ^ character, as the first character inside the brackets [],
 * specifies to match what's not in the brackets (i.e. - the characters you want to strip).
 *
 * @example
 * regexNumbersOnly.test("123")
 * // => false - returns true if test string is invalid
 *
 * "123abc".replace(regexNumbersOnly, "")
 * // => 123 - returns only digits
 */
export const regexNumbersOnly = /[^0-9.,]+/g;

/**
 * Method to allow only latin, symbol and numbers
 * All non-latin alphabets will be blocked
 *
 * @param event
 *
 * @example
 * @keypress="allowLatinSymbolsNumbers($event)"
 * @drop="allowLatinSymbolsNumbers($event)"
 * @paste="allowLatinSymbolsNumbers($event)"
 */
export const allowLatinSymbolsNumbers = (
  event: KeyboardEvent | ClipboardEvent | DragEvent
): void => {
  // check if event type is supported
  if (
    !includes(["keypress", "keydown", "keyup", "drop", "paste"], event.type)
  ) {
    return;
  }

  try {
    // set data to test
    let data = "";
    if (includes(["keypress", "keydown", "keyup"], event.type)) {
      event = <KeyboardEvent>event;
      data = event.key;
    } else if (event.type === "paste") {
      event = <ClipboardEvent>event;
      data = event.clipboardData?.getData("Text") || "";
    } else if (event.type === "drop") {
      event = <DragEvent>event;
      data = event.dataTransfer?.getData("Text") || "";
    }

    if (!regexLatinSymbolsNumbers.test(data)) {
      // Display error message when it is not a latin character
      makeToast(
        "error",
        i18nTranslate("Input not allowed."),
        i18nTranslate("Please only enter latin symbols and/or numbers.")
      );
    }
  } catch (error) {
    submitErrorToApi("Something went wrong when trying to handle input", {
      error,
    });
  }
};

/**
 * Keyboard event method to allow only alphanumeric and spaces only
 *
 * @param event
 * @returns boolean
 *
 * @example
 * @keypress="allowAlphanumericOrSpace($event)"
 */
export const allowAlphanumericOrSpace = (event: KeyboardEvent): boolean => {
  if (isRegexMatch(event.key, regex.alphanumericWithSpace)) {
    return true;
  } else {
    event.preventDefault();
    return false;
  }
};

/**
 * Clean an input string of non digits.
 * Period and comas will be retained
 *
 * @param strValue - string value to clean
 * @returns - cleaned value string
 *
 * @example
 * cleanNonDigits("123abc")
 * => "123"
 * cleanNonDigits("1.23abc")
 * => "1.23"
 */
export const cleanNonDigits = (
  strValue: string | null | undefined = ""
): string => {
  if (!strValue) return "";
  return strValue.toString().replace(regexNumbersOnly, "");
};

/**
 * Use similar to {@link allowLatinSymbolsNumbers}
 * @param event Event parameter. Pass using `$event`
 * @param unallowedStrings List of restricted strings that when detected, will cancel the event.
 */
export const restrictStrings = (
  event: KeyboardEvent | InputEvent | ClipboardEvent | DragEvent,
  unallowedStrings: string[]
) => {
  try {
    let data = "";

    if (event instanceof KeyboardEvent) {
      data = event.key;
    } else if (event instanceof InputEvent) {
      data = event.data ?? "";
    } else if (event instanceof ClipboardEvent) {
      data = event.clipboardData?.getData("Text") ?? "";
    } else if (event instanceof DragEvent) {
      data = event.dataTransfer?.getData("Text") ?? "";
    }

    for (const str of unallowedStrings) {
      if (data.includes(str)) {
        event.preventDefault();
      }
    }
  } catch (error) {
    submitErrorToApi("Something went wrong when trying to handle input", {
      error,
    });
  }
};

/**
 * Replace every comma-spaces in a string value
 * @param value input string value
 * @returns new string value without comma-spaces
 */
export const removeCommaSpaceFromInput = (value: string): string => {
  return value.replace(new RegExp(regex.commaSeparator, "g"), " ");
};
